import React, { useState } from "react";
import { Button, Typography } from "@mui/material";
import { Layout, SectionContent, SectionHead } from "@layouts/MainLayout";
import { useTranslation } from "react-i18next";
import TableList from "@components/TableList";
import { RowSelectionState } from "@tanstack/react-table";
import { columns } from "@pages/admin/users/_components/columns";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import useHandleError from "@lib/utils/errorHook";
import { useSnackbar } from "@context/SnackBarProvider";
import { getUsers } from "@lib/api/queries/users";
import { User, UsersList } from "@lib/types/users";
import ViewUser from "@pages/admin/users/_components/viewUser";
import CreateUser from "@pages/admin/users/_components/createUser";
import PromptDialog from "@components/PromptDialog";
import { deleteUser } from "@lib/api/mutations/users";
import { AxiosError } from "axios";

const UsersMain: React.FC = () => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const handleError = useHandleError();

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);

  const [showDeletePrompt, setShowDeletePrompt] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | undefined>(undefined);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  const {
    data: users,
    isFetching,
    refetch,
    error,
  } = useQuery<UsersList>({
    queryKey: ["users"],
    queryFn: () => getUsers(),
    refetchOnWindowFocus: false,
  });

  const handleViewUser = (user: User) => {
    setSelectedUser(user);
    setViewDialogOpen(true);
  };

  const { mutate: deleteDriverfn, isPending: isDeletingDriver } = useMutation({
    mutationFn: deleteUser,
    onSuccess: (data: any) => {
      showSnackbar(t("user.drivers.deleteSuccess"), "success");

      setSelectedUser(undefined);
      refetch();
    },
    onError: (error: AxiosError | unknown) => {
      if (error instanceof AxiosError && error.response) {
        const errorDetails = handleError(error.response.data);
        errorDetails && showSnackbar(errorDetails.message, "error");
      } else {
        showSnackbar(t("general.error"), "error");
      }
    },
  });

  const confirmDeleteUser = () => {
    if (!selectedUser) return;
    deleteDriverfn(selectedUser.id);
    setShowDeletePrompt(false);
  };

  const handleDeleteUser = (user: User) => {
    setSelectedUser(user);
    setShowDeletePrompt(true);
  };
  return (
    <Layout>
      <SectionHead title={t("admin.users.header")}>
        <div className="flex items-center gap-2">
          <Button
            variant="contained"
            onClick={() => {
              setCreateUserDialogOpen(true);
            }}
            color="primary"
            className="flex items-center gap-1 p-2"
          >
            <Plus size={18} />
            <Typography variant="body2">{t("admin.users.add")}</Typography>
          </Button>
        </div>
      </SectionHead>

      <SectionContent>
        {!error && (
          <div className="flex items-center justify-between">
            <TableList
              data={users?.data || []}
              columns={columns(handleViewUser, handleDeleteUser)}
              rowSelection={rowSelection}
              setRowSelection={setRowSelection}
              isLoading={isFetching}
            />
          </div>
        )}

        <CreateUser
          createUserDialogOpen={createUserDialogOpen}
          setCreateUserDialogOpen={setCreateUserDialogOpen}
          refetch={refetch}
        />

        <ViewUser
          viewUserDialogOpen={viewDialogOpen}
          setViewUserDialogOpen={setViewDialogOpen}
          userData={selectedUser}
        />

        <PromptDialog
          handleClose={() => setShowDeletePrompt(false)}
          handleConfirm={() => confirmDeleteUser()}
          isOpen={showDeletePrompt}
          title={t("admin.users.delete")}
          description={t("admin.users.deletePrompt", {
            name: selectedUser?.email,
          })}
        />
      </SectionContent>
    </Layout>
  );
};

export default UsersMain;
